import { formatDistanceToNow } from 'date-fns';
import { Calendar, ChevronDown, FileText } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { Submission } from '@/types';

interface TeamSubmissionProps {
  submission: Submission | null;
}

export function TeamSubmission({ submission }: TeamSubmissionProps) {
  const [showRawPayload, setShowRawPayload] = useState(false);

  if (!submission) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Submission</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center text-muted-foreground">
            No submission found
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Submission</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="font-medium text-muted-foreground text-sm">
              Source
            </div>
            <Badge variant="outline" className="capitalize">
              {submission.source}
            </Badge>
          </div>

          <div>
            <div className="font-medium text-muted-foreground text-sm">
              Type
            </div>
            <div className="text-sm">
              {submission.isTeam ? 'Team' : 'Individual'}
            </div>
          </div>

          {submission.submittedAt && (
            <div className="col-span-2">
              <div className="flex items-center gap-1 font-medium text-muted-foreground text-sm">
                <Calendar className="h-3 w-3" />
                Submitted At
              </div>
              <div className="text-sm">
                {new Date(submission.submittedAt).toLocaleString()} (
                {formatDistanceToNow(new Date(submission.submittedAt), {
                  addSuffix: true,
                })}
                )
              </div>
            </div>
          )}
        </div>

        {submission.rawPayload ? (
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto p-0 font-medium text-muted-foreground text-sm hover:bg-transparent"
              onClick={() => setShowRawPayload((v) => !v)}
            >
              <FileText className="mr-1 h-3 w-3" />
              Raw Payload
              <ChevronDown
                className={`ml-1 h-3 w-3 transition-transform ${
                  showRawPayload ? 'rotate-180' : ''
                }`}
              />
            </Button>
            {showRawPayload && (
              <div className="mt-2 max-h-[300px] overflow-auto rounded-md bg-muted p-3">
                <pre className="font-mono text-xs">
                  {JSON.stringify(submission.rawPayload, null, 2)}
                </pre>
              </div>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
